import {
  Card,
  Chip,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  Theme,
  createStyles,
  makeStyles,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  FiberManualRecord,
  FiberManualRecordOutlined,
  MoreVertRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, Link as RRLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { getCookie, updateCookie } from '../../../functions/manageCookies';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  Document,
  getDocumentsMasterDataAsync,
  updateActiveDocumentAsync
} from './documentSlice';

interface DocumentParamProps extends PaginationProps {}

const sortOptions = [
  { value: 'id', text: 'createdDate' },
  { value: 'name', text: 'name' }
];

export default function DocumentView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { masterData: documents, count } = useSelector(
    (state: RootState) => state.documents
  );
  const { user } = useSelector((state: RootState) => state.userProfile);

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const pageKey = `[DOC]-userId${user?.id}-masterData-documents`;
  const cookieParams: DocumentParamProps | undefined = getCookie(pageKey);

  const defaultParams: DocumentParamProps = {
    page: 0,
    limit: 30,
    sortBy: 'name',
    orderBy: ORDER.asc
  };

  const [documentParams, setDocumentParams] = useState<DocumentParamProps>({
    ...defaultParams,
    ...cookieParams
  });

  useEffect(() => {
    loadingDocument(documentParams);
  }, []);

  function loadingDocument(params: DocumentParamProps) {
    updateCookie(pageKey, params);
    dispatch(getDocumentsMasterDataAsync(params));
    setDocumentParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingDocument({
      ...documentParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, document?: Document) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && document) {
        setSelectedDocument(document);
      }
      setOpenOption(open);
    };
  }

  async function updateActiveDocument(documentId: number, isActive: boolean) {
    try {
      const document = { id: documentId, isActive };
      await dispatch(updateActiveDocumentAsync(document)).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('messages.success.statusUpdated')
        })
      );
    } catch {}
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedDocument?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            {selectedDocument && (
              <>
                <ListItem
                  component={NavLink}
                  to={`documents/${selectedDocument.id}/edit`}
                  button
                >
                  <ListItemIcon>
                    <EditRounded />
                  </ListItemIcon>
                  <ListItemText primary={t('masterData.document.actions.edit')} />
                </ListItem>
                <ListItem
                  button
                  onClick={() =>
                    updateActiveDocument(
                      selectedDocument.id,
                      !selectedDocument.isActive
                    )
                  }
                >
                  <ListItemIcon>
                    {!selectedDocument.isActive ? (
                      <FiberManualRecord className={classes.greenColor} />
                    ) : (
                      <FiberManualRecordOutlined color="action" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      !selectedDocument.isActive
                        ? t('masterData.document.status.active')
                        : t('masterData.document.status.inactive')
                    }
                  />
                </ListItem>
                <ListItem button onClick={() => {}} disabled>
                  <ListItemIcon>
                    <DeleteRounded />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('masterData.document.actions.delete')}
                    secondary={t('projectCard.functionNotAvailable')}
                  />
                </ListItem>
              </>
            )}
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{ masterDataName: t('masterData.document.title'), masterDataRef: 'documents' }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('masterData.document.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('masterData.document.title')}
        params={documentParams}
        sortOptions={sortOptions.map((s) => ({ ...s, text: t(`masterData.sortOptions.${s.text}`) }))}
        loadingElement={loadingDocument}
      />
      {documents.map((document) => {
        const discipline = disciplines.find(
          (discipline) => discipline.id === document.disciplineId
        );
        return (
          <Card className={classes.card} key={document.id}>
            <div className={classes.cardContainer}>
              <div className={classes.width50}>
                <Typography>
                  <Chip
                    className={classes.chip}
                    size="small"
                    variant="outlined"
                    label={discipline?.name}
                  />
                  {document.name}
                </Typography>
              </div>
              <div className={classes.width50}>
                <Typography>{document.no}</Typography>
              </div>
              <div className={classes.moreAction}>
                <Tooltip
                  title={
                    document.isActive
                      ? (t('masterData.document.status.active') as string)
                      : (t('masterData.document.status.inactive') as string)
                  }
                  arrow
                >
                  {document.isActive ? (
                    <FiberManualRecord className={classes.greenColor} />
                  ) : (
                    <FiberManualRecordOutlined color="action" />
                  )}
                </Tooltip>
                <Tooltip title={t('masterData.document.actions.more') as string} arrow>
                  <IconButton
                    size="small"
                    onClick={toggleDrawer(true, document)}
                  >
                    <MoreVertRounded />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / documentParams.limit)}
          shape="rounded"
          page={documentParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`documents/create`}
          variant="extended"
          color="primary"
          aria-label={t('masterData.document.actions.newAria') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('masterData.document.actions.new')}
        </Fab>
      </Zoom>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      },
      paddingRight: 54,
      position: 'relative',
      minHeight: 30
    },
    width50: {
      width: '50%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    moreAction: {
      position: 'absolute',
      top: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap'
    },
    chip: {
      margin: theme.spacing(0, 0.5, 0, -0.25)
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    },
    greenColor: {
      color: theme.palette.success.main
    }
  })
);
